// Coupons that grant something when redeemed (F-POS-06; spec/06 §8.5).
//
// A **coupon** grants rather than discounts (that is a promotion, F-POS-03, or a code, F-POS-02): a
// wallet bonus, loyalty points, a pass or a product voucher (spec/06 §8.5). It is created in a
// **batch** — a name, what every code grants, how many codes, single- or multi-use, a per-player
// limit and a validity window. Codes are ten characters from an unambiguous alphabet, generated with
// a cryptographic RNG and unique platform-wide (spec/06 §8.5).
//
// This build fulfils the **wallet** bonus grant (to the org-scoped `wallet_bonus` account) atomically
// on redemption — the one grant the ledger can post inside an organisation's scope today. Points
// (platform loyalty, F-LOY), passes (F-PRC) and product vouchers (F-CAT) ride in with their catalogues;
// the batch, the codes and the atomic redemption mechanism built here are the same for all of them, so
// `grantKind` is an enum with room to grow.
//
//   POST …/coupon-batches               create a batch and its codes (discounts:manage)
//   GET  …/coupon-batches               the organisation's batches, with issued/redeemed counts
//   POST …/venues/{v}/coupons/redeem    redeem a code for a registered player (orders:create)
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { CouponBatchIdSchema, CouponRedemptionIdSchema, PlayerIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { DISCOUNTS_MANAGE, ORDERS_CREATE } from "../permissions/index.js";
/** What a coupon grants when it is redeemed (spec/06 §8.5). `wallet` is fulfilled today; more follow. */
export const CouponGrantKindSchema = z
    .enum(['wallet'])
    .openapi('CouponGrantKind', { description: 'What a coupon grants (spec/06 §8.5).' });
/**
 * Where a batch's codes may be redeemed (spec/06 §8.5; owner 2026-09-30, #709): `organization` — at every venue of
 * the organisation — or `venues` — only at the venue(s) in `venueIds` (the console picks one). The default keeps what
 * a batch did before the field existed: none named is organisation-wide, some named is those venues.
 */
export const CouponScopeKindSchema = z
    .enum(['organization', 'venues'])
    .openapi('CouponScopeKind', { description: 'Where a coupon batch may be redeemed: the whole organisation, or only the named venue(s) (spec/06 §8.5).' });
const BATCH_NAME = z.string().min(1).max(120);
/** A coupon code: ten characters from the unambiguous alphabet (spec/06 §8.5). */
export const CouponCodeSchema = z
    .string()
    .regex(/^[0-9A-HJ-NP-Z]{10}$/, 'A coupon code is ten characters from the unambiguous alphabet.')
    .openapi('CouponCode');
/** The fields common to a batch and a just-created batch (kept as a shape so neither uses `allOf`). */
const couponBatchFields = {
    id: CouponBatchIdSchema,
    batchName: BATCH_NAME,
    grantKind: CouponGrantKindSchema,
    /** For `wallet`: the bonus each code grants. */
    grantAmount: NonNegativeMoneySchema.nullable(),
    /** How many codes were generated (spec/06 §8.5). */
    quantity: z.int().min(1),
    /** How many times one code may be redeemed — 1 is single-use (spec/06 §8.5). */
    maxRedemptionsPerCode: z.int().min(1),
    /** How many codes of the batch one player may redeem (default 1, spec/06 §8.5). */
    perPlayerLimit: z.int().min(1),
    validFrom: IsoDateTimeSchema.nullable(),
    validUntil: IsoDateTimeSchema.nullable(),
    /** `organization` (every venue) or `venues` (only those in `venueIds`). */
    scope: CouponScopeKindSchema,
    /** The venues it may be redeemed at, for `scope: venues`; empty for `scope: organization`. */
    venueIds: z.array(z.string()),
    active: z.boolean(),
    /** Codes generated, and codes redeemed at least once. */
    issuedCount: z.int().min(0),
    redeemedCount: z.int().min(0),
    createdAt: IsoDateTimeSchema,
};
/** A batch of coupons (spec/06 §8.5). */
export const CouponBatchSchema = z.object(couponBatchFields).openapi('CouponBatch');
/** A created batch, with its generated codes for export (spec/06 §8.5). */
export const CreatedCouponBatchSchema = z
    .object({ ...couponBatchFields, codes: z.array(CouponCodeSchema) })
    .openapi('CreatedCouponBatch');
export const CouponBatchListSchema = z.object({ batches: z.array(CouponBatchSchema) }).openapi('CouponBatchList');
/** Create a batch of coupons (spec/06 §8.5). Quantity is capped for this build; the spec allows 100,000. */
export const CreateCouponBatchSchema = z
    .object({
    batchName: BATCH_NAME,
    grantKind: CouponGrantKindSchema,
    grantAmount: NonNegativeMoneySchema.optional(),
    quantity: z.int().min(1).max(2000),
    maxRedemptionsPerCode: z.int().min(1).max(100_000).optional(),
    perPlayerLimit: z.int().min(1).max(100).optional(),
    validFrom: IsoDateTimeSchema.optional(),
    validUntil: IsoDateTimeSchema.optional(),
    /**
     * `organization` or `venues`. Absent: `venues` when `venueIds` names any, `organization` otherwise — what a batch
     * meant before the field existed. `venues` needs at least one venue of the organisation; `organization` names none.
     */
    scope: CouponScopeKindSchema.optional(),
    venueIds: z.array(z.string()).max(500).optional(),
})
    .openapi('CreateCouponBatchRequest');
/** Redeem a coupon for a player (spec/06 §8.5). Registered players only. */
export const RedeemCouponSchema = z
    .object({ code: CouponCodeSchema, playerId: PlayerIdSchema })
    .openapi('RedeemCouponRequest');
/** What a redemption granted (spec/06 §8.5). */
export const CouponRedemptionSchema = z
    .object({
    id: CouponRedemptionIdSchema,
    couponBatchId: CouponBatchIdSchema,
    batchName: BATCH_NAME,
    grantKind: CouponGrantKindSchema,
    grantAmount: NonNegativeMoneySchema.nullable(),
    redeemedAt: IsoDateTimeSchema,
})
    .openapi('CouponRedemption');
export const createCouponBatchRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/coupon-batches',
    operationId: 'createCouponBatch',
    tags: ['commerce'],
    summary: 'Create a batch of coupons (F-POS-06)',
    description: 'Generates a batch of unique coupon codes that grant a wallet bonus or points. Requires ' +
        '`discounts:manage`. Audited. The response carries the codes for export.',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateCouponBatchSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(CreatedCouponBatchSchema, 'The batch, with its generated codes.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        409: problemResponse('The grant does not match its kind.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listCouponBatchesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/coupon-batches',
    operationId: 'listCouponBatches',
    tags: ['commerce'],
    summary: 'List the organisation’s coupon batches (F-POS-06)',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(CouponBatchListSchema, 'The organisation’s batches, with counts.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const redeemCouponRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/coupons/redeem',
    operationId: 'redeemCoupon',
    tags: ['commerce'],
    summary: 'Redeem a coupon for a player (F-POS-06)',
    description: 'Redeems a coupon code for a registered player, granting a wallet bonus or points atomically. ' +
        'Requires `orders:create`. Rate limited to 10 failed attempts per player per hour. Audited.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: RedeemCouponSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(CouponRedemptionSchema, 'What the redemption granted.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such code, or no such player at this organisation.', 'not-found'),
        409: problemResponse('The code is used up, expired, or already redeemed by this player; or it is valid only at another venue.', 'conflict', 'coupon-not-valid-here'),
        429: problemResponse('Too many failed redemption attempts; try later.', 'rate-limited'),
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=coupons.js.map